import { useEffect, useState } from "react";
import { P, pub } from "@/lib/publicApi";
import { PublicReceipt } from "@/components/public/PublicReceipt";
import { CategoryPicker, L } from "@/components/public/ComplaintForm";
import { toast } from "sonner";

const field = "w-full px-3 py-2.5 text-sm rounded-sm border outline-none bg-white text-[#12263A] placeholder:text-[#94A3B8] focus:border-[#12263A]";

export default function CommendationForm({ onNavigate }) {
  const [cats, setCats] = useState([]);
  const [sel, setSel] = useState([]);
  const [anon, setAnon] = useState(false);
  const [f, setF] = useState({});
  const [busy, setBusy] = useState(false);
  const [receipt, setReceipt] = useState(null);
  useEffect(() => { pub.get("/categories").then((r) => setCats(r.data.commendation)).catch(() => {}); }, []);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const toggle = (c) => setSel((p) => (p.includes(c) ? p.filter((x) => x !== c) : [...p, c]));
  const submit = async () => {
    if (!sel.length) { toast.error("Select at least one commendation category."); return; }
    if ((f.description || "").trim().length < 20) { toast.error("Please describe what the employee did (at least 20 characters)."); return; }
    if (!(f.officer_name || "").trim()) { toast.error("Please provide the officer/employee name you are commending."); return; }
    if (!anon && !(f.name && (f.email || f.phone))) { toast.error("Provide your name and an email or phone — or submit anonymously."); return; }
    setBusy(true);
    try {
      const r = await pub.post("/cases", { kind: "commendation", categories: sel, anonymous: anon, ...f });
      setReceipt(r.data);
      window.scrollTo(0, 0);
    } catch (e) { toast.error(e.response?.data?.detail || "Submission failed — please try again."); }
    finally { setBusy(false); }
  };
  if (receipt) return <PublicReceipt result={receipt} onNavigate={onNavigate} />;
  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div className="flex items-center gap-4">
        <img src="/brand/ia-seal.png" alt="Matcom Internal Affairs seal" data-testid="commendation-ia-seal" className="w-20 h-20 rounded-full shrink-0" />
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.2em]" style={{ color: P.gold }}>Matcom Internal Affairs</div>
          <h2 className="text-2xl font-bold" style={{ color: P.navy }}>Submit a Commendation</h2>
          <p className="text-sm mt-1" style={{ color: P.dim }}>Recognize an employee for exceptional service. Commendations are routed to Community Relations, verified by the supervisor, acknowledged by command, and added to the employee's personnel record.</p>
        </div>
      </div>
      <label data-testid="commend-anonymous-toggle" className="flex items-start gap-3 p-4 rounded-sm border cursor-pointer" style={{ borderColor: anon ? P.navy : P.line, background: anon ? "#EEF2F6" : "#fff" }}>
        <input type="checkbox" checked={anon} onChange={(e) => setAnon(e.target.checked)} className="mt-1" />
        <span className="text-sm" style={{ color: P.ink }}><b>Submit anonymously</b> (no contact information required)</span>
      </label>
      {!anon && (
        <div className="p-4 rounded-sm border space-y-3" style={{ borderColor: P.line, background: "#fff" }}>
          <div className="text-xs font-bold uppercase tracking-wider" style={{ color: P.navy }}>Your Contact Information</div>
          <div><L>Full Name *</L><input data-testid="commend-name" className={field} style={{ borderColor: P.line }} value={f.name || ""} onChange={set("name")} /></div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div><L>Email</L><input data-testid="commend-email" className={field} style={{ borderColor: P.line }} value={f.email || ""} onChange={set("email")} /></div>
            <div><L>Telephone</L><input className={field} style={{ borderColor: P.line }} value={f.phone || ""} onChange={set("phone")} /></div>
          </div>
        </div>
      )}
      <div>
        <L>Commendation Categories * — select all that apply ({sel.length} selected)</L>
        <CategoryPicker options={cats} selected={sel} onToggle={toggle} testPrefix="commend" />
      </div>
      <div className="p-4 rounded-sm border space-y-3" style={{ borderColor: P.line, background: "#fff" }}>
        <div className="text-xs font-bold uppercase tracking-wider" style={{ color: P.navy }}>Employee &amp; Incident</div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div><L>Officer/Employee Name *</L><input data-testid="commend-officer" className={field} style={{ borderColor: P.line }} value={f.officer_name || ""} onChange={set("officer_name")} placeholder="Employee's name" /></div>
          <div><L>Badge / Unit # (if known)</L><input className={field} style={{ borderColor: P.line }} value={f.badge_number || ""} onChange={set("badge_number")} /></div>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div><L>Date of Incident</L><input data-testid="commend-date" type="date" className={field} style={{ borderColor: P.line }} value={f.incident_date || ""} onChange={set("incident_date")} /></div>
          <div><L>Location</L><input className={field} style={{ borderColor: P.line }} value={f.incident_location || ""} onChange={set("incident_location")} /></div>
        </div>
      </div>
      <div><L>Why are you commending this employee? *</L>
        <textarea data-testid="commend-description" className={field} style={{ borderColor: P.line }} rows={6} value={f.description || ""} onChange={set("description")} placeholder="Please describe what this employee did and why they deserve recognition…" /></div>
      <button data-testid="commend-submit" disabled={busy} onClick={submit}
        className="w-full py-3.5 text-sm font-bold text-white rounded-sm hover:opacity-90 disabled:opacity-50" style={{ background: P.green }}>
        {busy ? "SUBMITTING…" : "SUBMIT COMMENDATION"}
      </button>
    </div>
  );
}
